import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Clock,
  User,
  Buildings,
  MapPin,
  ArrowRight,
  CircleNotch,
  Eye,
} from "@phosphor-icons/react";
import { breadcrumbSchema, richItemListSchema } from "../lib/jsonLd";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecentlyVerified = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/recently-verified?limit=100`)
      .then(r => r.json())
      .then(d => setProviders(d.providers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Recently Verified Providers - Today's Lookups | NPI Lookup</title>
        <meta
          name="description"
          content="See the most recently looked-up and verified healthcare providers on NPI Lookup. Updated in real-time as users search."
        />
        <link rel="canonical" href="https://npilookup.com/recently-verified" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Recently Verified" },
        ]))}</script>
        {providers && providers.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(richItemListSchema(
            "Recently Verified Healthcare Providers",
            "Healthcare providers recently looked up and verified on NPI Lookup from the NPPES national registry.",
            providers,
            "/recently-verified",
          ))}</script>
        )}
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3">
              <Clock className="w-4 h-4 text-teal-300" />
              <span className="text-teal-200 text-sm font-medium">Live Updates</span>
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-2" data-testid="recently-verified-title">
              Recently Verified Providers
            </h1>
            <p className="text-slate-300 text-base max-w-xl mx-auto">
              The 100 most recently looked-up providers on NPI Lookup, updated in real-time.
            </p>
          </div>
        </section>

        {/* Breadcrumbs */}
        <nav className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-sm" data-testid="rv-breadcrumbs">
          <ol className="flex items-center gap-2 text-slate-500">
            <li><Link to="/" className="hover:text-teal">Home</Link></li>
            <li>/</li>
            <li className="text-slate-900 font-medium">Recently Verified</li>
          </ol>
        </nav>

        {/* Provider List */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <CircleNotch className="w-8 h-8 text-teal animate-spin" />
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500">No providers verified yet. Search to get started!</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="rv-list">
              {providers.map((p, i) => (
                <Link
                  key={p.npi}
                  to={`/provider/${p.npi}`}
                  className="flex items-center gap-4 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-teal/40 hover:shadow-sm transition-all group"
                  data-testid={`rv-item-${i}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    p.entity_type === "Individual" ? "bg-navy/10" : "bg-teal/10"
                  }`}>
                    {p.entity_type === "Individual" ? (
                      <User className="w-4 h-4 text-navy" />
                    ) : (
                      <Buildings className="w-4 h-4 text-teal" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate group-hover:text-teal transition-colors">
                      {p.name || "Unknown Provider"}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      <span className="font-mono">NPI {p.npi}</span>
                      {p.specialty && <span>{p.specialty}</span>}
                      {p.city && p.state && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" /> {p.city}, {p.state}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 shrink-0">
                    {p.view_count > 1 && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <Eye className="w-3 h-3" /> {p.view_count}
                      </span>
                    )}
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default RecentlyVerified;

/**
 * Sidebar/home widget — shows most recent 5 providers as a compact list.
 */
export const RecentlyVerifiedWidget = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/recently-verified?limit=5`)
      .then(r => r.json())
      .then(d => setProviders(d.providers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (providers.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rv-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-teal" />
          Recently Verified
        </h3>
        <Link to="/recently-verified" className="text-xs text-teal font-medium hover:underline">
          View all
        </Link>
      </div>
      <ul className="space-y-2">
        {providers.map((p) => (
          <li key={p.npi}>
            <Link
              to={`/provider/${p.npi}`}
              className="flex items-start gap-2 group"
            >
              <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 mt-0.5 ${
                p.entity_type === "Individual" ? "bg-navy/10" : "bg-teal/10"
              }`}>
                {p.entity_type === "Individual" ? (
                  <User className="w-3 h-3 text-navy" />
                ) : (
                  <Buildings className="w-3 h-3 text-teal" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate group-hover:text-teal transition-colors">
                  {p.name || "Unknown"}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {p.specialty}{p.city ? ` - ${p.city}, ${p.state}` : ""}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
